import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { chartTooltipProps } from "@/lib/chartTooltip";
import {
    formatPersen, labelPilihanProdi, labelTanpaLurus, LINGKUP_SEMUA, paramHasil, TEKS_HASIL_PERIODE_DIBUKA,
    KELOMPOK_KEPNAKES, teksLingkup, pilihanDariInstitusi, TEKS_KELOMPOK_KECIL_INSTITUSI,
} from "@/lib/survei";
import SurveiUnduhExcel from "@/components/SurveiUnduhExcel";
import SurveiRekapTabel from "@/components/SurveiRekapTabel";

// Satu seri (% responden Puas per dimensi) -> satu warna token tema, tanpa legenda; angka lengkap di tabel.
const WARNA_BATANG = "var(--brand-turq-text)";

// Tab Hasil: Lingkup (Semua prodi / Pilih prodi) + tanpa jawaban lurus memengaruhi layar DAN unduhan; angka dari server
// (SATU perhitungan dgn Excel). Periode masih dibuka -> tanpa angka. Pemuatan yg menampilkan angka tercatat di Audit Log.
// `jumlahButir` = jumlah butir versi periode (dari daftar periode) utk label opsi tanpa jawaban lurus.
// `kelompok` (PR #113): "kepnakes" -> lingkup Semua institusi / Pilih institusi (+ ZIP per institusi di Unduh Excel), pilihan
// dari `institusi_pilihan` server; tabel Rekap & perhitungan SAMA. Bawaan Kedokteran (tampilan tidak berubah).
export default function SurveiHasil({ periodeId, periodeAktif = false, jumlahButir = null, kelompok = "kedokteran" }) {
    const kepnakes = kelompok === KELOMPOK_KEPNAKES;
    const tl = teksLingkup(kelompok);
    const PILIH = tl.lingkupPilih;
    const [lingkup, setLingkup] = useState(LINGKUP_SEMUA);
    const [prodiId, setProdiId] = useState("");
    const [tanpaLurus, setTanpaLurus] = useState(false);
    const [data, setData] = useState(null);
    const [pilihan, setPilihan] = useState([]);
    useEffect(() => { setProdiId(""); setPilihan([]); }, [periodeId]);
    useEffect(() => { setLingkup(LINGKUP_SEMUA); }, [kelompok]);
    useEffect(() => {
        if (!periodeId) return;
        let batal = false; // jawaban permintaan lama (mis. ganti periode/prodi cepat) tidak menimpa yg terbaru
        setData(null);
        api.get(`/survei/periode/${periodeId}/hasil`, { params: paramHasil(lingkup, prodiId, tanpaLurus) })
            .then((r) => {
                if (batal) return;
                setData(r.data);
                setPilihan(r.data.kelompok === KELOMPOK_KEPNAKES ? pilihanDariInstitusi(r.data.institusi_pilihan) : (r.data.prodi_pilihan || []));
            })
            .catch((e) => { if (!batal) toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
        return () => { batal = true; };
    }, [periodeId, lingkup, prodiId, tanpaLurus]);

    if (!periodeId) return <p className="py-6 text-sm text-slate-500">Belum ada periode.</p>;
    const dibuka = data?.keadaan === "periode_dibuka";
    const prodiBoleh = !!pilihan.find((p) => p.prodi_id === prodiId && p.boleh);
    const kontrol = (
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center" data-testid="survei-hasil-kontrol">
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-600">Lingkup</span>
                <Select value={lingkup} onValueChange={(v) => { setLingkup(v); setProdiId(""); }} disabled={dibuka}>
                    <SelectTrigger className="w-44" data-testid="survei-lingkup"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={LINGKUP_SEMUA}>{tl.semua}</SelectItem>
                        <SelectItem value={PILIH}>{tl.pilih}</SelectItem>
                    </SelectContent>
                </Select>
                {lingkup === PILIH && (
                    <Select value={prodiId} onValueChange={setProdiId}>
                        <SelectTrigger className="w-full sm:w-80" data-testid="survei-pilih-prodi"><SelectValue placeholder={tl.placeholder} /></SelectTrigger>
                        <SelectContent>
                            {pilihan.map((p) => (
                                <SelectItem key={p.prodi_id} value={p.prodi_id} disabled={!p.boleh}>{labelPilihanProdi(p)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>
            {kepnakes && <p className="text-xs text-slate-500 md:basis-full" data-testid="survei-hasil-ket-institusi">{TEKS_KELOMPOK_KECIL_INSTITUSI}</p>}
            <div className="flex items-center gap-2">
                <Switch id="survei-tanpa-lurus" checked={tanpaLurus} onCheckedChange={setTanpaLurus} disabled={dibuka} data-testid="survei-tanpa-lurus" />
                <Label htmlFor="survei-tanpa-lurus" data-testid="survei-label-tanpa-lurus">{labelTanpaLurus(data?.jumlah_butir || jumlahButir)}</Label>
            </div>
        </div>
    );
    const unduh = (
        <SurveiUnduhExcel periodeId={periodeId} tanpaLurus={tanpaLurus} kosong={data?.total_jawaban === 0 || data?.responden_tercatat === 0}
                          periodeAktif={periodeAktif || dibuka} lingkup={lingkup} prodiId={prodiId} prodiBoleh={prodiBoleh} kelompok={kelompok} />
    );
    if (!data) return <div className="space-y-4" data-testid="survei-hasil">{kontrol}<p className="py-6 text-sm text-slate-500">Memuat…</p></div>;
    if (dibuka) {
        return (
            <div className="space-y-4" data-testid="survei-hasil">
                {unduh}
                <Card className="p-4" data-testid="survei-hasil-dibuka">
                    <p className="text-sm text-slate-700">{TEKS_HASIL_PERIODE_DIBUKA}</p>
                    <p className="mt-1 text-xs text-slate-500">Jawaban tercatat: {data.responden_tercatat.toLocaleString("id-ID")}</p>
                </Card>
            </div>
        );
    }
    if (lingkup === PILIH) {
        return (
            <div className="space-y-4" data-testid="survei-hasil">
                {kontrol}
                {unduh}
                {!prodiId ? <p className="py-6 text-sm text-slate-500">{kepnakes ? "Pilih institusi." : "Pilih program studi."}</p>
                    : <SurveiRekapTabel data={data.rekap} label={`Rekap ${kepnakes ? data.rekap.nama_institusi : data.rekap.nama_prodi}`} />}
            </div>
        );
    }
    const grafik = (data?.dimensi || []).map((d) => ({ label: d.kode, nama: d.nama, persen: d.persen }));
    return (
        <div className="space-y-4" data-testid="survei-hasil">
            {kontrol}
            {unduh}
            {!data.cukup ? <SurveiRekapTabel data={data.rekap} /> : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <Card className="p-4">
                            <div className="text-xs font-semibold uppercase text-slate-500">Responden dihitung</div>
                            <div className="text-2xl font-extrabold text-slate-900" data-testid="survei-hasil-responden">{data.responden.toLocaleString("id-ID")}</div>
                            {data.responden_tb != null && (
                                <div className="text-xs text-slate-500" data-testid="survei-hasil-responden-tb">
                                    Responden dengan {data.opsi_tb?.label || "TB"}: {data.responden_tb.toLocaleString("id-ID")}
                                </div>
                            )}
                        </Card>
                        <Card className="p-4">
                            <div className="text-xs font-semibold uppercase text-slate-500">Jawaban lurus</div>
                            <div className="text-2xl font-extrabold text-slate-900">{data.lurus.toLocaleString("id-ID")}</div>
                            <div className="text-xs text-slate-500">dari {data.total_jawaban.toLocaleString("id-ID")} jawaban{data.tanpa_lurus ? " (tidak dihitung)" : ""}</div>
                        </Card>
                        <Card className="p-4">
                            <div className="text-xs font-semibold uppercase text-slate-500">Rata-rata {data.dimensi.length} dimensi</div>
                            <div className="text-2xl font-extrabold text-slate-900" data-testid="survei-hasil-rata">{formatPersen(data.rata_rata)}</div>
                        </Card>
                    </div>
                    <p className="text-xs text-slate-500">
                        Puas = rata-rata butir dimensi itu ≥ {data.ambang_puas}. Persentase = responden Puas dibagi seluruh responden yang dihitung.
                        {data.responden_tb ? ` ${data.opsi_tb?.label || "TB"} diabaikan dalam rata-rata; dimensi yang seluruh butirnya ${data.opsi_tb?.label || "TB"} tidak dihitung dan responden tetap masuk pembagi.` : ""}
                    </p>
                    <SurveiRekapTabel data={data.rekap} label="Rekap seluruh responden" />
                    <Card className="p-4">
                        <div className="h-[420px]" aria-hidden="true">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={grafik} layout="vertical" margin={{ left: 0, right: 16 }} barCategoryGap={6}>
                                    <CartesianGrid horizontal={false} stroke="hsl(var(--border))" />
                                    <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                                    <YAxis type="category" dataKey="label" width={36} tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                                    <Tooltip {...chartTooltipProps} cursor={{ fill: "hsl(var(--muted))" }}
                                             labelFormatter={(_, p) => p?.[0]?.payload?.nama || ""}
                                             formatter={(v) => [formatPersen(v), "Puas"]} />
                                    <Bar dataKey="persen" fill={WARNA_BATANG} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="compact w-full" data-testid="survei-hasil-tabel">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                                        <th>Kode</th><th>Dimensi</th><th className="text-right">Butir</th>
                                        <th className="text-right">Puas</th><th className="pr-4 text-right">% Puas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.dimensi.map((d) => (
                                        <tr key={d.kode} className="border-t border-slate-200">
                                            <td className="font-semibold">{d.kode}</td>
                                            <td>{d.nama}</td>
                                            <td className="text-right tabular-nums">{d.jumlah_butir}</td>
                                            <td className="text-right tabular-nums">{d.puas.toLocaleString("id-ID")}</td>
                                            <td className="pr-4 text-right font-semibold tabular-nums">{formatPersen(d.persen)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}
